import type { LibForm } from '../components/FormModal.js';
import { isScreener } from './scoreHistory.js';
import { sendState, sentBackAt, sentName, type SentFormRow } from './sentForms.js';

/**
 * ehr#165 - the client drawer's Forms tab as one table (Chase's AVAILABLE_FORMS_F): every
 * form with its status for this client, grouped Filled for this client / Waiting on client /
 * Not filled yet, with a filter tab per group. Pure helpers, no DOM.
 *
 * A form is filled by staff in the EHR (form submissions) or by the client through the
 * portal (a Forms Hub send that came back, portal#61). A send still open puts the form in
 * Waiting on client. Bundles, and sends of a form no longer in the library, get rows of
 * their own.
 */

/** Row look: done = filled (green), sent = sent and not opened (blue), inc = started or
 *  overdue (amber), none = never filled. */
export type RowState = 'done' | 'sent' | 'inc' | 'none';
export type RowGroup = 'done' | 'pend' | 'none';
export type FormsFilter = 'all' | RowGroup;
/** ehr#640: the kind filter on the one Forms tab (the Screeners tab folded into it). */
export type FormKind = 'all' | 'forms' | 'screeners';

/**
 * ehr#640 - is this row a screener (or assessment)? A library form that isScreener says so
 * (a System screener, or a Form Builder form with scoring or scored items, ehr#363/#429).
 * Bundles and sends of a form no longer in the library count as forms.
 */
export const rowIsScreener = (r: FormRow): boolean => r.form !== null && isScreener(r.form);

/** ehr#640 - the rows the kind filter keeps. */
export function kindRows(rows: FormRow[], kind: FormKind): FormRow[] {
  if (kind === 'all') return rows;
  return rows.filter((r) => rowIsScreener(r) === (kind === 'screeners'));
}

/** A form the staff filled in the EHR (GET /contacts/:id/form-submissions). */
export interface StaffFill {
  id: string;
  formId: string;
  submittedBy: string;
  createdAt: string;
  /** credifyfbs#203: saved unfinished (Save as draft, ehr#480, or required fields still blank). */
  draft?: boolean;
}

export interface FormRow {
  /** 'form:<formId>', or 'send:<dispatchId>' for a bundle / a form not in the library. */
  key: string;
  name: string;
  meta: string;
  source: 'System' | 'Admin' | 'Bundle' | 'Form';
  /** The library form (New, Fill again, Preview, History); null for a send-only row. */
  form: LibForm | null;
  /** The send the row is about: the open one, or a send-only row's own. */
  send: SentFormRow | null;
  state: RowState;
  group: RowGroup;
  /** "Filled Sep 22, 2026", "Sent, not opened", "Never filled"... */
  status: string;
  /** The line under it ("by Marcus Rivera · 4 days ago"); '' when none. */
  detail: string;
  /** Staff fills plus portal returns of this form, for History. */
  historyCount: number;
  /** ehr#481: a send-only row's History: the client's returns of the same bundle (or form),
   *  newest first. */
  historySends?: SentFormRow[];
  /** Sort key: when it was last filled or sent (ms). */
  at: number;
  /** credifyfbs#203: the latest fill is this staff draft; the row's first button finishes it. */
  draftId?: string;
}

export const GROUP_LABELS: Record<RowGroup, string> = {
  done: 'Filled for this client',
  pend: 'Waiting on client',
  none: 'Not filled yet',
};

/** How many Not filled rows show before Show all. */
export const UNFILLED_PAGE = 8;

export const fmtDay = (v: string | number | Date): string =>
  new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtShort = (v: string | number | Date): string =>
  new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** "today", "1 day ago", "4 days ago" (whole local days). */
export function daysAgo(v: string | number | Date, now: Date = new Date()): string {
  const a = new Date(v);
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const d = Math.max(0, Math.round((end - start) / 86400000));
  return d === 0 ? 'today' : d === 1 ? '1 day ago' : `${d} days ago`;
}

/** One-line meta under a form title: the screener descriptor (system), or
 *  "N fields · by Owner" (custom). */
export function formMeta(f: LibForm): string {
  if (f.source === 'system') return f.description || 'Built-in screener';
  const count = `${f.fields?.length ?? 0} fields`;
  const by = f.owner ? `by ${f.owner}` : f.description || 'Org form';
  return `${count} · ${by}`;
}

const time = (v: string | null | undefined) => (v ? new Date(v).getTime() || 0 : 0);

/** An open send's state, status and detail lines. */
function pending(send: SentFormRow, now: Date): Pick<FormRow, 'state' | 'status' | 'detail'> {
  const st = sendState(send, now);
  const sent = `Sent ${fmtShort(send.sentAt)}`;
  if (st === 'overdue')
    return {
      state: 'inc',
      status: 'Overdue',
      detail: send.dueDate ? `${sent} · due ${fmtShort(send.dueDate)}` : sent,
    };
  if (st === 'started') {
    const saved = send.submissions[0]?.updatedAt;
    return {
      state: 'inc',
      status: send.packetId ? 'In progress' : 'Started, not submitted',
      detail: saved ? `${sent} · last saved ${fmtShort(saved)}` : sent,
    };
  }
  return { state: 'sent', status: 'Sent, not opened', detail: `${sent} to the client portal` };
}

export interface BuildInput {
  /** Every library form (system + custom). */
  forms: LibForm[];
  /** Whether a library form passes the contact-type filter; a form with anything for this
   *  client shows whatever the filter says. */
  shown: (f: LibForm) => boolean;
  fills: StaffFill[];
  sent: SentFormRow[];
  userName: (id: string) => string;
  now?: Date;
}

export function buildFormRows({
  forms,
  shown,
  fills,
  sent,
  userName,
  now = new Date(),
}: BuildInput): FormRow[] {
  const byId = new Map(forms.map((f) => [f.id, f]));
  const rows: FormRow[] = [];
  for (const f of forms) {
    const staff = fills
      .filter((s) => s.formId === f.id)
      .sort((a, b) => time(b.createdAt) - time(a.createdAt));
    const sends = sent.filter((s) => !s.packetId && s.formId === f.id);
    const back = sends.filter((s) => sendState(s, now) === 'done');
    const open = sends
      .filter((s) => sendState(s, now) !== 'done')
      .sort((a, b) => time(b.sentAt) - time(a.sentAt));
    const historyCount = staff.length + back.length;
    if (!historyCount && !open.length && !shown(f)) continue;
    const base = {
      key: `form:${f.id}`,
      name: f.name,
      meta: formMeta(f),
      source: f.source === 'system' ? ('System' as const) : ('Admin' as const),
      form: f,
      historyCount,
    };
    if (open[0]) {
      rows.push({
        ...base,
        send: open[0],
        group: 'pend',
        at: time(open[0].sentAt),
        ...pending(open[0], now),
      });
      continue;
    }
    if (historyCount) {
      // The latest fill, by staff or by the client.
      const staffAt = time(staff[0]?.createdAt);
      const lastBack = back.sort((a, b) => time(sentBackAt(b)) - time(sentBackAt(a)))[0];
      const backAt = lastBack ? time(sentBackAt(lastBack)) : 0;
      const at = Math.max(staffAt, backAt);
      const who =
        staffAt >= backAt && staff[0]
          ? `by ${userName(staff[0].submittedBy)}`
          : 'by the client, in the portal';
      // credifyfbs#203: the latest fill is a staff draft: amber, and the first button finishes it.
      if (staffAt >= backAt && staff[0]?.draft) {
        rows.push({
          ...base,
          send: null,
          group: 'done',
          state: 'inc',
          status: `Draft saved ${fmtDay(at)}`,
          detail: `${who} · not finished yet`, // ehr#480: a draft can be saved on purpose now
          at,
          draftId: staff[0].id,
        });
        continue;
      }
      rows.push({
        ...base,
        send: null,
        group: 'done',
        state: 'done',
        status: `Filled ${fmtDay(at)}`,
        detail: `${who} · ${historyCount > 1 ? `filled ${historyCount} times` : daysAgo(at, now)}`,
        at,
      });
      continue;
    }
    rows.push({
      ...base,
      send: null,
      group: 'none',
      state: 'none',
      status: 'Never filled',
      detail: '',
      at: 0,
    });
  }
  // ehr#481: sends of the same bundle (or of the same form the library no longer has).
  const sameSend = (a: SentFormRow, b: SentFormRow) =>
    b.packetId
      ? a.packetId === b.packetId
      : !a.packetId && (b.formId ? a.formId === b.formId : sentName(a) === sentName(b));
  // Bundles, and sends of a form the library no longer has: a row each.
  for (const s of sent) {
    if (!s.packetId && s.formId && byId.has(s.formId)) continue;
    const done = sendState(s, now) === 'done';
    const historySends = sent
      .filter((o) => sameSend(o, s) && sendState(o, now) === 'done')
      .sort((a, b) => time(sentBackAt(b)) - time(sentBackAt(a)));
    const base = {
      key: `send:${s.id}`,
      name: sentName(s),
      meta: s.packetId ? 'Bundle sent through the portal' : 'Sent through the portal',
      source: s.packetId ? ('Bundle' as const) : ('Form' as const),
      form: null,
      send: s,
      historyCount: historySends.length,
      historySends,
    };
    rows.push(
      done
        ? {
            ...base,
            group: 'done',
            state: 'done',
            status: `Filled ${fmtDay(sentBackAt(s))}`,
            detail: `by the client, in the portal · ${daysAgo(sentBackAt(s), now)}`,
            at: time(sentBackAt(s)),
          }
        : { ...base, group: 'pend', at: time(s.sentAt), ...pending(s, now) },
    );
  }
  return rows;
}

/**
 * ehr#276 - how well a form's name fits the search, best first: 0 the name is the search,
 * 1 it starts with it, 2 the search starts a word in it, 3 the name has it anywhere, 4 the
 * name has every word, 5 only the meta line / description does. A library number ("4 - ")
 * at the start of the name is ignored.
 */
export function searchRank(name: string, text: string): number {
  const q = text.toLowerCase().trim().replace(/\s+/g, ' ');
  const full = name.toLowerCase().replace(/\s+/g, ' ');
  const plain = full.replace(/^\s*\d+\s*[-–.)]\s*/, '').trim();
  if (plain === q) return 0;
  if (plain.startsWith(q)) return 1;
  if (full.includes(' ' + q) || full.startsWith(q)) return 2;
  if (full.includes(q)) return 3;
  if (q.split(' ').every((w) => full.includes(w))) return 4;
  return 5;
}

/**
 * Search over the name and the meta line, every word, any order and case. ehr#276: the
 * matches come back best first (searchRank), ties in their original order.
 */
export function searchRows(rows: FormRow[], text: string): FormRow[] {
  const words = text.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return rows;
  return rows
    .filter((r) => {
      const hay = `${r.name} ${r.meta} ${r.form?.description ?? ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    })
    .map((r, i) => ({ r, i, k: searchRank(r.name, text) }))
    .sort((a, b) => a.k - b.k || a.i - b.i)
    .map((x) => x.r);
}

/**
 * The rows of each group, in display order: newest first, Not filled in library order.
 * ehr#276: `ranked` (a search is on) keeps the order searchRows gave, best match first.
 */
export function grouped(rows: FormRow[], ranked = false): Record<RowGroup, FormRow[]> {
  const pick = (g: RowGroup) => rows.filter((r) => r.group === g);
  const newest = (list: FormRow[]) => (ranked ? list : list.sort((a, b) => b.at - a.at));
  return {
    done: newest(pick('done')),
    pend: newest(pick('pend')),
    none: pick('none'),
  };
}
